

// src/components/employer/ApplicantsDashboard.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Briefcase, MapPin, Users, UserPlus, ArrowLeft } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import {
  PlusCircle,
  Mail,
  Send,
  X,
  User,
  Phone,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import VirtualCall from '../../interview/InterviewMain';
import { toast } from 'sonner';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

const statusColors = {
  Applied: 'bg-blue-100 text-blue-800',
  Shortlisted: 'bg-purple-100 text-purple-800',
  Interviewed: 'bg-yellow-100 text-yellow-800',
  Hired: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
};


function DetailSection({ title, children }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-gray-100 py-1.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium text-right">{value || '—'}</span>
    </div>
  );
}



export default function ApplicantsDashboard() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
const [isVirtualOpen, setIsVirtualOpen] = useState(false);
  const [isBrowseOpen, setIsBrowseOpen] = useState(false);
const [allJobseekers, setAllJobseekers] = useState([]);
const [jobseekersLoading, setJobseekersLoading] = useState(false);
const [jobseekerSearch, setJobseekerSearch] = useState('');
const [selectedJobseeker, setSelectedJobseeker] = useState(null); // detail view target
const [addingId, setAddingId] = useState(null); // tracks in-flight "add as employee"
  // Modals
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
const [virtual, setVirtual] = useState(false)
  // Form states
  const [createForm, setCreateForm] = useState({
    jobTitle: '',
    jobDescription: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    notes: '',
  });

  const [emailForm, setEmailForm] = useState({
    subject: '',
    body: '',
  });

  const [statusForm, setStatusForm] = useState({
    status: '',
    note: '',
  });

  // Email history
  const [emailHistory, setEmailHistory] = useState([]);

  // Fetch applicants on mount
  useEffect(() => {
    fetchApplicants();
  }, []);

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}non-registered-application/myapplicants`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setApplicants(res.data.data || []);
      }
    } catch (err) {
      setError('Failed to load applicants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Stats calculations
  const totalApplicants = applicants.length;
  const statusCounts = applicants.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});

  const statusData = Object.entries(statusCounts).map(([name, value], index) => ({
    name,
    value,
    fill: COLORS[index % COLORS.length],
  }));

  // Handlers
  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      console.log(token)
      const res = await axios.post(`${process.env.REACT_APP_API_URL}non-registered-application`, createForm, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        toast.success("successfully added")
        setApplicants((prev) => [res.data.data, ...prev]);
        setIsCreateOpen(false);
        setCreateForm({
          jobTitle: '',
          jobDescription: '',
          fullName: '',
          email: '',
          phoneNumber: '',
          notes: '',
        });
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create applicant');
    }
  };

  const openEmailModal = (applicant) => {
    setSelectedApplicant(applicant);
    setEmailForm({ subject: '', body: '' });
    setIsEmailOpen(true);
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}non-registered-application/${selectedApplicant._id}/send-email`,
        emailForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        alert('Email sent successfully');
        setIsEmailOpen(false);
        fetchApplicants(); // Refresh list to update email count if needed
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send email');
    }
  };

  const openHistoryModal = async (applicant) => {
    setSelectedApplicant(applicant);
    setEmailHistory([]); // Clear previous history
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}non-registered-application/${applicant._id}/email-history`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setEmailHistory(res.data.data || []);
      setIsHistoryOpen(true);
    } catch (err) {
      alert('Failed to load email history');
    }
  };

  const openStatusModal = (applicant) => {
    setSelectedApplicant(applicant);
    setStatusForm({ status: applicant.status, note: '' });
    setIsStatusOpen(true);
  };


  const handleStatusChange = (e) => {
    const { name, value } = e.target;
    setStatusForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}non-registered-application/${selectedApplicant._id}`,
        statusForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        // Refresh applicants list
        fetchApplicants();
        setIsStatusOpen(false);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status');
    }
  };


  const openBrowseModal = () => {
  setIsBrowseOpen(true);
  setSelectedJobseeker(null);
  setJobseekerSearch('');
  fetchAllJobseekers();
};

const fetchAllJobseekers = async () => {
  try {
    setJobseekersLoading(true);
    const token = localStorage.getItem('token');
    const res = await axios.get(`${process.env.REACT_APP_API_URL}jobseeker/alljobseekers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // your controller returns the array directly, not wrapped in { success, data }
    const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
    setAllJobseekers(data);
  } catch (err) {
    console.error(err);
    toast.error('Failed to load jobseekers');
  } finally {
    setJobseekersLoading(false);
  }
};

const getFullName = (js) =>
  `${js.firstName || ''} ${js.middleName ? js.middleName + ' ' : ''}${js.lastName || ''}`.trim();

// client-side filter across name, state, LGA, gender, and other schema fields
const filteredJobseekers = allJobseekers.filter((js) => {
  const q = jobseekerSearch.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    js.firstName,
    js.lastName,
    js.middleName,
    js.email,
    js.phoneNumber,
    js.whatsappNumber,
    js.state,
    js.localGovernment,
    js.country,
    js.gender,
    js.job,
    js.jobMode,
    js.jobDecision,
    js.address,
    js.address2,
    js.race,
    js.disability,
  ];
  return haystack.some((field) => field && field.toString().toLowerCase().includes(q));
});

const handleAddAsEmployee = async (jobseeker) => {
  const fullName = getFullName(jobseeker);

  if (!jobseeker.email || !jobseeker.phoneNumber) {
    toast.error(`${fullName || 'This jobseeker'} is missing an email or phone number`);
    return;
  }

  setAddingId(jobseeker._id);
  try {
    const token = localStorage.getItem('token');
    const payload = {
      jobTitle: jobseeker.job || 'General Application',
      jobDescription: '',
      fullName,
      email: jobseeker.email,
      phoneNumber: jobseeker.phoneNumber,
      notes: `Added directly from jobseeker database (Jobseeker ID: ${jobseeker._id})`,
    };

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}non-registered-application`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (res.data.success) {
      toast.success(`${fullName} added as an applicant`);
      setApplicants((prev) => [res.data.data, ...prev]);
    }
  } catch (err) {
    toast.error(err.response?.data?.message || `Failed to add ${fullName}`);
  } finally {
    setAddingId(null);
  }
};

  return (
    <div className="space-y-8 mt-20 pb-16">
      {/* Header + Create Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Applicants</h1>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center px-5 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          Add New Applicant
        </button>
     
      </div>
      <div>
      <button
        onClick={() => setIsVirtualOpen(true)}
        className="px-6 py-3 bg-blue-600 text-white rounded-lg"
      >
        Generate Virtual Call Link
      </button>

      <VirtualCall isOpen={isVirtualOpen} onClose={() => setIsVirtualOpen(false)} />
    </div>

    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Applicants</h1>
  <div className="flex gap-3">
    <button
      onClick={openBrowseModal}
      className="inline-flex items-center px-5 py-3 bg-white border border-indigo-600 text-indigo-600 rounded-xl hover:bg-indigo-50 transition-colors shadow-sm"
    >
      <Users className="h-5 w-5 mr-2" />
      Browse Jobseekers
    </button>
    <button
      onClick={() => setIsCreateOpen(true)}
      className="inline-flex items-center px-5 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
    >
      <PlusCircle className="h-5 w-5 mr-2" />
      Add New Applicant
    </button>
  </div>
</div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <div className="bg-blue-100 rounded-xl shadow-sm border p-6">
          <p className="text-sm text-gray-600">Total Applicants</p>
          <p className="text-3xl font-bold text-indigo-700 mt-2">{totalApplicants}</p>
        </div>

        <div className="bg-green-100 rounded-xl shadow-sm border p-6">
          <p className="text-sm text-gray-600">Hired</p>
          <p className="text-3xl font-bold text-green-700 mt-2">
            {statusCounts.Hired || 0}
          </p>
        </div>
         <div className="bg-purple-100 rounded-xl shadow-sm border p-6">
          <p className="text-sm text-gray-600">Shortlisted</p>
          <p className="text-3xl font-bold text-blue-700 mt-2">
            {statusCounts.Shortlisted || 0}
          </p>
        </div>


        <div className="bg-yellow-100 rounded-xl shadow-sm border p-6">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-3xl font-bold text-blue-700 mt-2">
            {(statusCounts.Applied || 0) + (statusCounts.Shortlisted || 0)}
          </p>
        </div>

        <div className="bg-red-100 rounded-xl shadow-sm border p-6">
          <p className="text-sm text-gray-600">Rejected</p>
          <p className="text-3xl font-bold text-red-700 mt-2">
            {statusCounts.Rejected || 0}
          </p>
        </div>
      </div>

      {/* Status Pie Chart */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4">Application Status Breakdown</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={statusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                label={({ name, percent }) =>
                  percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                }
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Applicants Table */}
        <h6 className="text-sm font-bold text-gray-900">Please Note that the default password of your applicants to their jobseeker account is <span className='text-orange-500 font-bold'>jobseeker123</span>  which can be changed on their dashboard</h6>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-6 border-b">

          <h3 className="text-xl font-semibold text-gray-900">Applicants List</h3>
        
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Phone
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Job Title
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {applicants.map((app) => (
                <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{app.seeker.fullName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {app.seeker.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {app.seeker.phoneNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {app.jobTitle}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusColors[app.status]
                      }`}
                    >
                      {app.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex gap-4 justify-end">
                    <button
                      onClick={() => openEmailModal(app)}
                      className="text-indigo-600 hover:text-indigo-800"
                      title="Send Email"
                    >
                      <Mail size={18} />
                    </button>
                    <button
                      onClick={() => openHistoryModal(app)}
                      className="text-blue-600 hover:text-blue-800"
                      title="View Email History"
                    >
                      <Clock size={18} />
                    </button>
                    <button
                      onClick={() => openStatusModal(app)}
                      className="text-green-600 hover:text-green-800"
                      title="Update Status"
                    >
                      <Edit size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Applicant Modal */}
      <Transition show={isCreateOpen}>
        <Dialog onClose={() => setIsCreateOpen(false)} className="relative z-50">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8">
                  <div className="flex items-center justify-between mb-6">
                    <DialogTitle className="text-xl font-bold text-gray-900">
                      Add New Applicant
                    </DialogTitle>
                    <button
                      onClick={() => setIsCreateOpen(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <form onSubmit={handleCreateSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Title *
                      </label>
                      <input
                        type="text"
                        name="jobTitle"
                        value={createForm.jobTitle}
                        onChange={handleCreateChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={createForm.fullName}
                        onChange={handleCreateChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={createForm.email}
                        onChange={handleCreateChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={createForm.phoneNumber}
                        onChange={handleCreateChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Description (optional)
                      </label>
                      <textarea
                        name="jobDescription"
                        value={createForm.jobDescription}
                        onChange={handleCreateChange}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes (optional)
                      </label>
                      <textarea
                        name="notes"
                        value={createForm.notes}
                        onChange={handleCreateChange}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="flex justify-end gap-4 mt-8">
                      <button
                        type="button"
                        onClick={() => setIsCreateOpen(false)}
                        className="px-5 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        Add Applicant
                      </button>
                    </div>
                  </form>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
          </Dialog>
        </Transition>

        {/* Send Email Modal */}
        <Transition show={isEmailOpen}>
          <Dialog onClose={() => setIsEmailOpen(false)} className="relative z-50">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/40" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <TransitionChild
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8">
                    <div className="flex items-center justify-between mb-6">
                      <DialogTitle className="text-xl font-bold text-gray-900">
                        Send Email to {selectedApplicant?.seeker?.fullName}
                      </DialogTitle>
                      <button onClick={() => setIsEmailOpen(false)} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                      </button>
                    </div>

                    <form onSubmit={handleSendEmail} className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subject *
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={emailForm.subject}
                          onChange={handleEmailChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Interview Invitation"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message *
                        </label>
                        <textarea
                          name="body"
                          value={emailForm.body}
                          onChange={handleEmailChange}
                          required
                          rows={6}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Dear [Name],\n\nWe are pleased to invite you for an interview..."
                        />
                      </div>

                      <div className="flex justify-end gap-4 mt-8">
                        <button
                          type="button"
                          onClick={() => setIsEmailOpen(false)}
                          className="px-5 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                        >
                          <Send size={18} />
                          Send Email
                        </button>
                      </div>
                    </form>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Email History Modal */}
        <Transition show={isHistoryOpen}>
          <Dialog onClose={() => setIsHistoryOpen(false)} className="relative z-50">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/40" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <TransitionChild
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8">
                    <div className="flex items-center justify-between mb-6">
                      <DialogTitle className="text-xl font-bold text-gray-900">
                        Email History – {selectedApplicant?.seeker?.fullName}
                      </DialogTitle>
                      <button onClick={() => setIsHistoryOpen(false)} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                      </button>
                    </div>

                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                      {emailHistory.length > 0 ? (
                        emailHistory.map((log) => (
                          <div
                            key={log._id || log.sentAt}
                            className="border border-gray-200 rounded-lg p-5 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium text-gray-900">{log.subject}</p>
                                <p className="text-sm text-gray-500 mt-1">
                                  Sent by {log.sentBy?.email || 'Admin'} •{' '}
                                  {format(new Date(log.sentAt), 'dd MMM yyyy • HH:mm')}
                                </p>
                              </div>
                              <span
                                className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                                  log.status === 'sent'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {log.status.toUpperCase()}
                              </span>
                            </div>

                            <div className="mt-3 text-sm text-gray-700 whitespace-pre-line border-t pt-3">
                              {log.body}
                            </div>

                            {log.errorMessage && (
                              <div className="mt-3 p-3 bg-red-50 text-red-700 rounded border border-red-200 text-sm">
                                Error: {log.errorMessage}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-10 text-gray-500">
                          No emails have been sent to this applicant yet.
                        </div>
                      )}
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Update Status Modal */}
        <Transition show={isStatusOpen}>
          <Dialog onClose={() => setIsStatusOpen(false)} className="relative z-50">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/40" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <TransitionChild
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
                    <div className="flex items-center justify-between mb-6">
                      <DialogTitle className="text-xl font-bold text-gray-900">
                        Update Status – {selectedApplicant?.seeker?.fullName}
                      </DialogTitle>
                      <button onClick={() => setIsStatusOpen(false)} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                      </button>
                    </div>

                    <form onSubmit={handleUpdateStatus} className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Status *
                        </label>
                        <select
                          name="status"
                          value={statusForm.status}
                          onChange={handleStatusChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="Applied">Applied</option>
                          <option value="Shortlisted">Shortlisted</option>
                          <option value="Interviewed">Interviewed</option>
                          <option value="Hired">Hired</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Note / Reason (optional)
                        </label>
                        <textarea
                          name="note"
                          value={statusForm.note}
                          onChange={handleStatusChange}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="e.g. Selected for next round interview"
                        />
                      </div>

                      <div className="flex justify-end gap-4 mt-8">
                        <button
                          type="button"
                          onClick={() => setIsStatusOpen(false)}
                          className="px-5 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                          Update Status
                        </button>
                      </div>
                    </form>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Browse Jobseekers Modal */}
<Transition show={isBrowseOpen}>
  <Dialog onClose={() => setIsBrowseOpen(false)} className="relative z-50">
    <TransitionChild
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-black/40" />
    </TransitionChild>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl p-8 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                {selectedJobseeker && (
                  <button
                    onClick={() => setSelectedJobseeker(null)}
                    className="text-gray-500 hover:text-gray-700 mr-1"
                    title="Back to list"
                  >
                    <ArrowLeft size={20} />
                  </button>
                )}
                {selectedJobseeker ? getFullName(selectedJobseeker) : 'All Jobseekers'}
              </DialogTitle>
              <button
                onClick={() => setIsBrowseOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {selectedJobseeker ? (
              // ---------- DETAIL VIEW ----------
              <div className="overflow-y-auto pr-2 space-y-6">
                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                  <div>
                    <p className="text-sm text-gray-500">Applying / add as employee for job:</p>
                    <p className="font-medium text-gray-900">
                      {selectedJobseeker.job || 'Not specified'}
                    </p>
                  </div>
                  <button
                    disabled={addingId === selectedJobseeker._id}
                    onClick={() => handleAddAsEmployee(selectedJobseeker)}
                    className="inline-flex items-center px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UserPlus size={18} className="mr-2" />
                    {addingId === selectedJobseeker._id ? 'Adding...' : 'Add as Employee'}
                  </button>
                </div>

                <DetailSection title="Personal Information">
                  <DetailRow label="Full Name" value={getFullName(selectedJobseeker)} />
                  <DetailRow label="Email" value={selectedJobseeker.email} />
                  <DetailRow label="Phone Number" value={selectedJobseeker.phoneNumber} />
                  <DetailRow label="WhatsApp Number" value={selectedJobseeker.whatsappNumber} />
                  <DetailRow label="Gender" value={selectedJobseeker.gender} />
                  <DetailRow label="Date of Birth" value={selectedJobseeker.dateOfBirth} />
                  <DetailRow label="Race" value={selectedJobseeker.race} />
                  <DetailRow label="Disability" value={selectedJobseeker.disability} />
                </DetailSection>

                <DetailSection title="Location">
                  <DetailRow label="Address" value={selectedJobseeker.address} />
                  <DetailRow label="Address 2" value={selectedJobseeker.address2} />
                  <DetailRow label="State" value={selectedJobseeker.state} />
                  <DetailRow label="Local Government" value={selectedJobseeker.localGovernment} />
                  <DetailRow label="Country" value={selectedJobseeker.country} />
                </DetailSection>

                <DetailSection title="Job Preferences">
                  <DetailRow label="Desired Job" value={selectedJobseeker.job} />
                  <DetailRow label="Job Mode" value={selectedJobseeker.jobMode} />
                  <DetailRow label="Job Decision" value={selectedJobseeker.jobDecision} />
                  <DetailRow label="Expected Salary" value={selectedJobseeker.jobDecisionSalary} />
                </DetailSection>

                {selectedJobseeker.jobAssignments?.length > 0 && (
                  <DetailSection title="Job Assignments">
                    {selectedJobseeker.jobAssignments.map((ja, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-3 text-sm">
                        <p className="font-medium text-gray-900">{ja.jobTitle}</p>
                        <p className="text-gray-600">Status: {ja.status}</p>
                        {ja.reason && <p className="text-gray-600">Reason: {ja.reason}</p>}
                      </div>
                    ))}
                  </DetailSection>
                )}

                {selectedJobseeker.education?.length > 0 && (
                  <DetailSection title="Education">
                    {selectedJobseeker.education.map((ed, i) => (
                      <pre key={i} className="text-xs bg-gray-50 rounded p-3 whitespace-pre-wrap">
                        {JSON.stringify(ed, null, 2)}
                      </pre>
                    ))}
                  </DetailSection>
                )}

                {selectedJobseeker.workExperience?.length > 0 && (
                  <DetailSection title="Work Experience">
                    {selectedJobseeker.workExperience.map((we, i) => (
                      <pre key={i} className="text-xs bg-gray-50 rounded p-3 whitespace-pre-wrap">
                        {JSON.stringify(we, null, 2)}
                      </pre>
                    ))}
                  </DetailSection>
                )}
              </div>
            ) : (
              // ---------- LIST VIEW ----------
              <>
                <div className="relative mb-5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={jobseekerSearch}
                    onChange={(e) => setJobseekerSearch(e.target.value)}
                    placeholder="Search by name, state, LGA, gender, job, etc."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="overflow-y-auto pr-2 flex-1">
                  {jobseekersLoading ? (
                    <div className="text-center py-10 text-gray-500">Loading jobseekers...</div>
                  ) : filteredJobseekers.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">No jobseekers found.</div>
                  ) : (
                    <div className="space-y-3">
                      {filteredJobseekers.map((js) => (
                        <div
                          key={js._id}
                          className="flex items-center justify-between border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{getFullName(js)}</p>
                            <p className="text-sm text-gray-500">{js.email}</p>
                            <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                              {js.gender && <span>{js.gender}</span>}
                              {(js.state || js.localGovernment) && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={12} />
                                  {[js.localGovernment, js.state].filter(Boolean).join(', ')}
                                </span>
                              )}
                              {js.job && (
                                <span className="flex items-center gap-1">
                                  <Briefcase size={12} />
                                  {js.job}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 shrink-0">
                            <button
                              onClick={() => setSelectedJobseeker(js)}
                              className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                            >
                              View Details
                            </button>
                            <button
                              disabled={addingId === js._id}
                              onClick={() => handleAddAsEmployee(js)}
                              className="inline-flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <UserPlus size={16} className="mr-1" />
                              {addingId === js._id ? 'Adding...' : 'Add as Employee'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogPanel>
        </TransitionChild>
      </div>
    </div>
  </Dialog>
</Transition>
      </div>
    );
  }







































